'use client';

/**
 * A list that stops growing (VALE-1407).
 *
 * Several lists in the product render one row per item with no bound at all,
 * which is fine at the handful they were designed against and floods the screen
 * on a bulk run: thirty-six uploaded documents is a ~2,300px column that pushes
 * the surface's own actions off the bottom. This is that fix, once, rather than
 * a fourth hand-rolled copy of it.
 *
 * Past {@link maxVisible} rows it bounds the list, in one of two ways:
 *
 * - **`scroll`** caps the height and scrolls inside, with **no control at all**.
 *   A "+N more" button in front of a scroll box is a click that buys the reader
 *   nothing: they end up scrolling either way, so the button is pure ceremony.
 *   Right where the list sits above an action they must still reach — an upload
 *   step's "Analyse" button — because the height never changes.
 * - **`expand`** shows the first few behind a "+N more" control and reveals the
 *   rest in place. Right where the list is the content rather than a step, and
 *   the page is already scrollable around it, so there is no box to scroll in
 *   and the control is the only thing standing between a long list and the
 *   surface below it.
 *
 * At or below the threshold the rows are rendered bare, with no wrapper of any
 * kind, so adopting this on an existing list moves no pixels for the counts
 * that list already handled.
 */

import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/** Rows shown before the rest collapse behind the "+N more" control. */
export const DEFAULT_MAX_VISIBLE = 5;

export interface CollapsibleRowsProps {
  /** One node per row. Length decides whether anything collapses. */
  rows: ReactNode[];
  /** Rows shown while collapsed. `Infinity` disables collapsing entirely. */
  maxVisible?: number;
  /** What opening does. See the note above. */
  onOpen?: 'scroll' | 'expand';
  /** Height of the scroll box when `onOpen` is `scroll`. */
  openMaxHeightClassName?: string;
  /** Singular noun for the control: "+3 more files". */
  noun?: string;
  /** Applied to the rows container in every state. */
  className?: string;
  /**
   * Spread onto the rows container, so a caller keeps its own semantics — a
   * `role="list"` with an accessible name, typically. Dropping these in the
   * extraction turned a labelled list into a bare `div`, which is an
   * accessibility regression no visual check would have caught.
   */
  containerProps?: HTMLAttributes<HTMLDivElement>;
}

export function CollapsibleRows({
  rows,
  maxVisible = DEFAULT_MAX_VISIBLE,
  onOpen = 'scroll',
  openMaxHeightClassName = 'max-h-[min(60vh,34rem)]',
  noun = 'file',
  className,
  containerProps
}: CollapsibleRowsProps) {
  const [open, setOpen] = useState(false);

  const bounded = rows.length > maxVisible;
  const scrolls = bounded && onOpen === 'scroll';
  // Only `expand` ever hides rows; `scroll` shows them all inside its box.
  const collapsed = bounded && onOpen === 'expand' && !open;
  const shown = collapsed ? rows.slice(0, maxVisible) : rows;
  const hidden = rows.length - shown.length;

  const body = (
    <div
      {...containerProps}
      className={cn(
        className,
        // `overscroll-contain` so reaching the end does not chain the scroll
        // out into the dialog behind it.
        scrolls && [
          openMaxHeightClassName,
          'overflow-y-auto',
          'overscroll-contain',
          'pr-1'
        ]
      )}
    >
      {shown}
    </div>
  );

  // `scroll` needs no control, and a short list needs no wrapper at all.
  if (!bounded || onOpen === 'scroll') return body;

  return (
    <div className="flex flex-col gap-2">
      {body}
      {/* Wrapped so the alignment is the layout's: <Button> type-errors on any
          className that sets colour, size or alignment, and is right to. */}
      <div className="self-start">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          tone="muted"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {open ? (
            <>
              <ChevronUp className="size-4" aria-hidden />
              Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="size-4" aria-hidden />
              {`+${hidden} more ${noun}${hidden === 1 ? '' : 's'}`}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
